import { useState, useEffect, useCallback } from 'react';
import { ArrowUpDown, Tag, Pencil, AlertTriangle } from 'lucide-react';
import { marginCorrectionsApi } from '../../services/marginCorrectionsApi';
import { SearchableSelect } from '../ui/SearchableSelect';
import { useToast } from '../ui/Toast';
import { LineOriginPanel } from './LineOriginPanel';
import { CorrectCostModal } from './CorrectCostModal';
import type { InvoiceLineRow, InvoiceLinesSummary, MCFilterOptions } from '../../types/marginCorrections';

const fmt = (n: number | null, d = 2) => (n == null ? '–' : n.toLocaleString('de-CH', { minimumFractionDigits: d, maximumFractionDigits: d }));
const EMPTY_SUMMARY: InvoiceLinesSummary = {
    lineCount: 0, totalNetSales: 0, totalGrossProfit: 0, totalOriginalGrossProfit: 0,
    totalCorrectionDelta: 0, correctedLines: 0, specialLotLines: 0, avgMarginPct: null, avgOriginalMarginPct: null,
};

interface Props {
    company: string;
    filterOptions: MCFilterOptions | null;
    canEdit: boolean;
    refreshKey: number;
    onDataChanged: () => void;
}

export function InvoiceLinesTab({ company, filterOptions, canEdit, refreshKey, onDataChanged }: Props) {
    const { toast } = useToast();
    const [rows, setRows] = useState<InvoiceLineRow[]>([]);
    const [summary, setSummary] = useState<InvoiceLinesSummary>(EMPTY_SUMMARY);
    const [total, setTotal] = useState(0);
    const [appliedFrom, setAppliedFrom] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const [customerFilter, setCustomerFilter] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const [specialOnly, setSpecialOnly] = useState(false);
    const [correctedOnly, setCorrectedOnly] = useState(false);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('PostingDate');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    // Left-click panel + right-click modal targets
    const [panelDoc, setPanelDoc] = useState<string | null>(null);
    const [correctRow, setCorrectRow] = useState<InvoiceLineRow | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await marginCorrectionsApi.getInvoiceLines({
                company,
                customerID: customerFilter || undefined,
                productID: productFilter || undefined,
                specialLotsOnly: specialOnly || undefined,
                correctedOnly: correctedOnly || undefined,
                page, limit: 50, sortBy, sortOrder,
            });
            setRows(res.rows);
            setSummary(res.summary);
            setTotal(res.pagination.total);
            setAppliedFrom(res.appliedDateFrom);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to load invoice lines', 'error');
        } finally {
            setLoading(false);
        }
    }, [company, customerFilter, productFilter, specialOnly, correctedOnly, page, sortBy, sortOrder, toast]);

    useEffect(() => { load(); }, [load, refreshKey]);
    useEffect(() => { setPage(1); }, [company, customerFilter, productFilter, specialOnly, correctedOnly, sortBy, sortOrder]);

    const handleSort = (col: string) => {
        if (sortBy === col) setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
        else { setSortBy(col); setSortOrder('desc'); }
    };

    const handleSaved = () => {
        setCorrectRow(null);
        setPanelDoc(null);
        load();
        onDataChanged();
    };

    const totalPages = Math.ceil(total / 50);

    const SortH = ({ col, label, align }: { col: string; label: string; align?: 'right' }) => (
        <th className={`py-2 px-2 cursor-pointer hover:bg-gray-100 whitespace-nowrap ${align === 'right' ? 'text-right' : ''}`} onClick={() => handleSort(col)}>
            <span className="inline-flex items-center gap-1">{label}{sortBy === col && <ArrowUpDown className="w-3 h-3" />}</span>
        </th>
    );

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <SearchableSelect options={filterOptions?.customers ?? []} value={customerFilter} onChange={setCustomerFilter} placeholder="All Customers" className="min-w-[220px]" />
                <SearchableSelect options={filterOptions?.products ?? []} value={productFilter} onChange={setProductFilter} placeholder="All Products" className="min-w-[220px]" />
                <label className="flex items-center gap-1.5 text-sm text-gray-600"><input type="checkbox" checked={specialOnly} onChange={e => setSpecialOnly(e.target.checked)} className="rounded" /><Tag className="w-3.5 h-3.5" /> Special lots</label>
                <label className="flex items-center gap-1.5 text-sm text-gray-600"><input type="checkbox" checked={correctedOnly} onChange={e => setCorrectedOnly(e.target.checked)} className="rounded" /><Pencil className="w-3.5 h-3.5" /> Corrected</label>
                <span className="text-sm text-gray-400 ml-auto">{total} line(s){appliedFrom && <> · from {new Date(appliedFrom).toLocaleDateString('de-CH')}</>}</span>
            </div>

            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Tile label="Lines" value={String(summary.lineCount)} />
                <Tile label="Net Sales" value={`${fmt(summary.totalNetSales, 0)}`} sub="EUR" />
                <Tile label="Gross Profit" value={`${fmt(summary.totalGrossProfit, 0)}`} sub={summary.totalCorrectionDelta !== 0 ? `${summary.totalCorrectionDelta > 0 ? '+' : ''}${fmt(summary.totalCorrectionDelta, 0)} corrected` : 'EUR'} accent={summary.totalCorrectionDelta !== 0} />
                <Tile label="Avg Margin" value={summary.avgMarginPct != null ? `${fmt(summary.avgMarginPct, 1)}%` : '–'} />
                <Tile label="Corrected / Special" value={`${summary.correctedLines} / ${summary.specialLotLines}`} />
            </div>

            {/* Hint */}
            <p className="text-xs text-gray-400">Left-click a row for its origin · right-click to correct its cost.</p>

            {/* Table */}
            <div className="overflow-auto max-h-[calc(100vh-360px)] border rounded-lg">
                <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white z-10 shadow-sm">
                        <tr className="text-left text-xs text-gray-500 border-b">
                            <SortH col="PostingDate" label="Date" />
                            <SortH col="DocumentCode" label="Document" />
                            <SortH col="CustomerName" label="Customer" />
                            <SortH col="ProductName" label="Product" />
                            <th className="py-2 px-2">Lots</th>
                            <SortH col="Quantity" label="Qty" align="right" />
                            <SortH col="NetSalesEUR" label="Net Sales" align="right" />
                            <th className="py-2 px-2 text-right">Unit Cost</th>
                            <SortH col="GrossProfitEUR" label="Gross Profit" align="right" />
                            <SortH col="MarginPct" label="Margin" align="right" />
                            <th className="py-2 px-2 w-10"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={11} className="py-12 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" /></td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-gray-400">No invoice lines found</td></tr>}
                        {!loading && rows.map((r, i) => (
                            <tr
                                key={`${r.documentID}-${i}`}
                                onClick={() => setPanelDoc(r.documentID)}
                                onContextMenu={e => { if (canEdit) { e.preventDefault(); setCorrectRow(r); } }}
                                className={`border-b cursor-pointer ${panelDoc === r.documentID ? 'bg-blue-50' : r.isLowMargin ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}`}
                            >
                                <td className="py-1.5 px-2 whitespace-nowrap">{new Date(r.postingDate).toLocaleDateString('de-CH')}</td>
                                <td className="py-1.5 px-2 font-mono text-xs">{r.documentCode}</td>
                                <td className="py-1.5 px-2 max-w-[180px] truncate" title={r.customerName}>{r.customerName}</td>
                                <td className="py-1.5 px-2 max-w-[180px] truncate" title={r.productName}>{r.productName}</td>
                                <td className="py-1.5 px-2 font-mono text-xs text-gray-500 max-w-[120px] truncate" title={r.lotCodes}>{r.lotCodes || '–'}</td>
                                <td className="py-1.5 px-2 text-right">{fmt(r.quantity, 0)}</td>
                                <td className="py-1.5 px-2 text-right">{fmt(r.netSalesEUR, 0)}</td>
                                <td className="py-1.5 px-2 text-right">
                                    {r.correctionDeltaEUR !== 0 && r.unitCost != null
                                        ? <span title={`was ${fmt(r.originalCostEUR / (r.quantity || 1))}`}>{fmt(r.unitCost)}</span>
                                        : fmt(r.unitCost)}
                                </td>
                                <td className="py-1.5 px-2 text-right">
                                    {r.correctionDeltaEUR !== 0 && <span className="text-gray-400 line-through mr-1 text-xs">{fmt(r.originalGrossProfitEUR, 0)}</span>}
                                    {fmt(r.grossProfitEUR, 0)}
                                </td>
                                <td className={`py-1.5 px-2 text-right font-medium ${r.marginPct != null && r.marginPct < 0 ? 'text-red-600' : r.isLowMargin ? 'text-amber-600' : 'text-green-600'}`}>{r.marginPct != null ? `${fmt(r.marginPct, 1)}%` : '–'}</td>
                                <td className="py-1.5 px-2">
                                    <div className="flex items-center gap-1 justify-end">
                                        {r.isSpecialLot && <Tag className="w-3.5 h-3.5 text-purple-500" />}
                                        {r.isCorrected && <Pencil className="w-3.5 h-3.5 text-blue-500" />}
                                        {r.marginPct != null && r.marginPct < 0 && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Page {page} of {totalPages}</span>
                    <div className="flex gap-2">
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50">Previous</button>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50">Next</button>
                    </div>
                </div>
            )}

            {panelDoc && (
                <LineOriginPanel
                    company={company}
                    documentID={panelDoc}
                    canEdit={canEdit}
                    onClose={() => setPanelDoc(null)}
                    onCorrect={() => {
                        const row = rows.find(r => r.documentID === panelDoc);
                        if (row) setCorrectRow(row);
                    }}
                />
            )}

            {correctRow && (
                <CorrectCostModal
                    company={company}
                    source={correctRow}
                    dateFrom={appliedFrom}
                    onClose={() => setCorrectRow(null)}
                    onSaved={handleSaved}
                />
            )}
        </div>
    );
}

function Tile({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
    return (
        <div className={`rounded-lg border p-3 ${accent ? 'border-blue-200 bg-blue-50' : 'bg-white'}`}>
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-lg font-semibold text-gray-900">{value}</div>
            {sub && <div className={`text-xs ${accent ? 'text-blue-600' : 'text-gray-400'}`}>{sub}</div>}
        </div>
    );
}
